/**
 * FormPage.tsx
 * Main form component for creating and editing pitch submissions.
 * Handles form state management and submission logic using Formik.
 */

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik, Form } from "formik";
import ReleaseInfo from "./components/ReleaseInfo/ReleaseInfo";
import PlatformSelection from "./components/PlatformSelector/PlatformSelection";
import GeneralReleaseInfo from "./components/GeneralReleaseInfo/GeneralReleaseInfo";
import DspSpecificInfo from "./components/DSPSpecificInfo/DspSpecificInfo";
import { SelectedDSPs, FormPageProps } from "../PitchFormPage/types/form";
import {
  createPitchValidationSchema,
  PitchFormValues,
} from "./components/Schema/pitchValidationSchema";

import { pitchesApi } from "../repositories/pitches";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import { dspApi } from "../repositories/dsp";

const FormPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    submissionData = {},
    releaseData = {},
    isEditing = false,
  } = (location.state as FormPageProps) || {};

  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [isDrafting, setIsDrafting] = React.useState<boolean>(false);
  // Remove showErrorSummary state since we'll use toast instead

  // Initialize selected platforms
  const [selectedDSPs, setSelectedDSPs] = React.useState<SelectedDSPs>({
    spotify: Boolean(submissionData?.selectedPlatforms?.includes("spotify")),
    amazon: Boolean(submissionData?.selectedPlatforms?.includes("amazon")),
    deezer: Boolean(submissionData?.selectedPlatforms?.includes("deezer")),
    audiomack: Boolean(
      submissionData?.selectedPlatforms?.includes("audiomack")
    ),
    soundcloud: Boolean(
      submissionData?.selectedPlatforms?.includes("soundcloud")
    ),
    apple: Boolean(submissionData?.selectedPlatforms?.includes("apple")),
    TIDAL: Boolean(submissionData?.selectedPlatforms?.includes("TIDAL")),
    youtube: Boolean(submissionData?.selectedPlatforms?.includes("youtube")),
    email: Boolean(submissionData?.selectedPlatforms?.includes("email")),
  });

  // Initial form values
  const initialValues: PitchFormValues = {
    // Basic Artist and Release Information
    artistName: releaseData?.artist || "",
    releaseTitle: releaseData?.release || "",
    releaseDate: releaseData?.releaseDate || "",
    configuration: submissionData?.configuration || "",

    // Identifiers
    releaseUpc: submissionData?.releaseUpc || "",
    focusTrackName: submissionData?.focusTrackName || "",
    trackIsrc: submissionData?.trackIsrc || "",

    // Artist Details
    featuredArtists: submissionData?.featuredArtists || "",
    featuredArtistUri: submissionData?.featuredArtistUri || "",
    artistHometown: submissionData?.artistHometown || "",
    artistCurrentTown: submissionData?.artistCurrentTown || "",

    // Release Descriptions
    altTitles: submissionData?.altTitles || "",
    shortDescription: submissionData?.shortDescription || "",
    longDescription: submissionData?.longDescription || "",
    lyrics: submissionData?.lyrics || "",
    songDescription: submissionData?.songDescription || [],

    // Musical Characteristics
    mainGenre: submissionData?.mainGenre || [],
    songMood: submissionData?.songMood || [],
    language: submissionData?.language || [],

    // Links and Media
    spotifyUri: submissionData?.spotifyUri || "",
    linkToListen: submissionData?.linkToListen || "",
    promoPhotos: submissionData?.promoPhotos || "",
    socialsLinks: submissionData?.socialsLinks || "",

    // Platform-Specific Details
    similarArtists: submissionData?.similarArtists || "",
    asin: submissionData?.asin || "",
    TIDALpage: submissionData?.TIDALpage || "",
    soundCloudProfileUrl: submissionData?.soundCloudProfileUrl || "",
    soundCloudContact: submissionData?.soundCloudContact || "",
    distributor: submissionData?.distributor || "",
    appleId: submissionData?.appleId || "",
    appleTrackId: submissionData?.appleTrackId || "",
    

    // Platform-Specific Flags and Priorities
    pinToThisIsPlaylist: submissionData?.pinToThisIsPlaylist || false,
    addAmazonVoiceIntro: submissionData?.addAmazonVoiceIntro || false,
    pitchToAudiomackTrending: submissionData?.pitchToAudiomackTrending || false,
    isPremierProgram: submissionData?.isPremierProgram || false,

    //     // Subgenres
    spotifySubgenre: submissionData?.spotifySubgenre || [],
    amazonSubgenre: submissionData?.amazonSubgenre || [],
    deezerSubgenre: submissionData?.deezerSubgenre || [],
    audiomackSubgenre: submissionData?.audiomackSubgenre || [],
    TIDALSubgenre: submissionData?.TIDALSubgenre || [],
    soundCloudSubgenre: submissionData?.soundCloudSubgenre || [],

    // Spotify specific
    musicCulture: submissionData?.musicCulture || [],
    instruments: submissionData?.instruments || [],
    songStyle: submissionData?.songStyle || [],

    // Amazon specific
    listenersLocation: submissionData?.listenersLocation || [],
    songCategory: submissionData?.songCategory || [],

    // Deezer specific
    priority: submissionData?.priority || "",
    deezerEditorialTeam: submissionData?.deezerEditorialTeam || "",

    // TIDAL specific
    artistCountry: submissionData?.artistCountry || "",
    LabelDistributor: submissionData?.LabelDistributor || "",

    // SoundCloud specific
    promotionTerritories: submissionData?.promotionTerritories || [],
    freshMusicFriday: submissionData?.freshMusicFriday || [],
    playlistConsideration: submissionData?.playlistConsideration || "",
    soundCloudReleaseStage: submissionData?.soundCloudReleaseStage || "",

    // Status
    status: submissionData?.status || "submitted",

    // YouTube specific
    youtubePromotionSurface: submissionData?.youtubePromotionSurface || [],
    youtubeReleaseStage: submissionData?.youtubeReleaseStage || "",
    // youtubeSongDetails: submissionData?.youtubeSongDetails || [],
    youtubePromotionDate: submissionData?.youtubePromotionDate || "",
    youtubeTerritories: submissionData?.youtubeTerritories || [],
    //assets
    images: submissionData?.images || [],
  };

  function transformSubmissionData(formData: any) {
    const dsps: any = {};

    if (selectedDSPs.spotify) {
      dsps.spotify = {
        spotifyUri: formData.spotifyUri,
        artistHometown: formData.artistHometown,
        artistCurrentTown: formData.artistCurrentTown,
        pinToPlaylist: formData.pinToThisIsPlaylist,
        subgenre: Array.isArray(formData.spotifySubgenre)
          ? formData.spotifySubgenre.join(", ")
          : formData.spotifySubgenre,
        musicCulture: Array.isArray(formData.musicCulture)
          ? formData.musicCulture.join(", ")
          : formData.musicCulture,
        instruments: Array.isArray(formData.instruments)
          ? formData.instruments.join(", ")
          : formData.instruments,
        songStyle: Array.isArray(formData.songStyle)
          ? formData.songStyle.join(", ")
          : formData.songStyle,
      };
    }

    if (selectedDSPs.amazon) {
      dsps.amazon = {
        addAmazonVoiceIntro: formData.addAmazonVoiceIntro,
        listenersLocation: Array.isArray(formData.listenersLocation)
          ? formData.listenersLocation.join(", ")
          : formData.listenersLocation,
        similarArtists: formData.similarArtists,
        songCategory: Array.isArray(formData.songCategory)
          ? formData.songCategory.join(", ")
          : formData.songCategory,
        subgenre: Array.isArray(formData.amazonSubgenre)
          ? formData.amazonSubgenre.join(", ")
          : formData.amazonSubgenre,
        asin: formData.asin,
      };
    }

    if (selectedDSPs.deezer) {
      dsps.deezer = {
        priority: formData.priority,
        editorialTeam: formData.deezerEditorialTeam,
        subgenre: Array.isArray(formData.deezerSubgenre)
          ? formData.deezerSubgenre.join(", ")
          : formData.deezerSubgenre,
      };
    }

    if (selectedDSPs.TIDAL) {
      dsps.tidal = {
        subgenre: Array.isArray(formData.TIDALSubgenre)
          ? formData.TIDALSubgenre.join(", ")
          : formData.TIDALSubgenre,
        artistPageLink: formData.TIDALpage,
        country: formData.artistCountry,
        labelDistributor: formData.LabelDistributor,
        socialHandles: formData.socialsLinks,
      };
    }

    if (selectedDSPs.audiomack) {
      dsps.audiomack = {
        pitchToTrending: formData.pitchToAudiomackTrending,
        subgenre: Array.isArray(formData.audiomackSubgenre)
          ? formData.audiomackSubgenre.join(", ")
          : formData.audiomackSubgenre,
      };
    }
    if (selectedDSPs.soundcloud) {
      dsps.soundcloud = {
        // fullName: formData.fullName,
        soundCloudProfileUrl: formData.soundCloudProfileUrl,

        soundCloudContact: formData.soundCloudContact,
        subgenre: Array.isArray(formData.soundCloudSubgenre)
          ? formData.soundCloudSubgenre.join(", ")
          : formData.soundCloudSubgenre,
        isPremierProgram: formData.isPremierProgram,
        distributor: formData.distributor,
        promotionTerritories: Array.isArray(formData.promotionTerritories)
          ? formData.promotionTerritories.join(", ")
          : formData.promotionTerritories,
        freshMusicFriday: Array.isArray(formData.freshMusicFriday)
          ? formData.freshMusicFriday.join(", ")
          : formData.freshMusicFriday,
        playlistConsideration: formData.playlistConsideration,
        releaseStage: formData.soundCloudReleaseStage,
      };
    }

    if (selectedDSPs.apple) {
      dsps.apple = {
        appleId: formData.appleId,
        appleTrackId: formData.appleTrackId,
      };
    }

    if (selectedDSPs.youtube) {
      dsps.youtube = {
        promotionSurface: Array.isArray(formData.youtubePromotionSurface)
          ? formData.youtubePromotionSurface.join(", ")
          : formData.youtubePromotionSurface,
        songDetails: formData.youtubeReleaseStage,
        // songDetails: Array.isArray(formData.youtubeSongDetails)
        //   ? formData.youtubeSongDetails.join(", ")
        //   : formData.youtubeSongDetails,
        promotionDate: formData.youtubePromotionDate,
        territories: Array.isArray(formData.youtubeTerritories)
          ? formData.youtubeTerritories.join(", ")
          : formData.youtubeTerritories,
      };
    }

    return {
      artistName: formData.artistName,
      releaseTitle: formData.releaseTitle,
      releaseDate: formData.releaseDate,
      configuration: formData.configuration,
      releaseUPC: formData.releaseUpc === "" ? null : formData.releaseUpc,
      focusTrackName: formData.focusTrackName,
      trackISRC: formData.trackIsrc === "" ? null : formData.trackIsrc,
      featuredArtists: formData.featuredArtists,
      featuredArtistURI: formData.featuredArtistUri,
      altTitles: formData.altTitles,
      shortDescription: formData.shortDescription,
      longDescription: formData.longDescription,
      lyrics: formData.lyrics,
      songDescription: Array.isArray(formData.songDescription)
        ? formData.songDescription.join(", ")
        : formData.songDescription,
      mainGenre: Array.isArray(formData.mainGenre)
        ? formData.mainGenre.join(", ")
        : formData.mainGenre,
      songMood: Array.isArray(formData.songMood)
        ? formData.songMood.join(", ")
        : formData.songMood,
      language: Array.isArray(formData.language)
        ? formData.language.join(", ")
        : formData.language,
      linkToListen: formData.linkToListen,
      promoPhotos: formData.promoPhotos,
      status: formData.status,
      dsps: dsps,
      isEmail: selectedDSPs.email,
      images: formData.images || [],
    };
  }

  const handleSubmit = async (values: PitchFormValues) => {
    setIsSubmitting(true);

    const submissionPayload = {
      ...values,
      status: "submitted",
      selectedPlatforms: Object.entries(selectedDSPs)
        .filter(([_, isSelected]) => isSelected)
        .map(([platform]) => platform),
    };
    try {
      const response = await pitchesApi.create(
        transformSubmissionData(submissionPayload)
      );

      if (!response?.data.success) {
        throw new Error(response.statusText || "Submission failed");
      }

      navigate("/dashboard");
    } catch (error: any) {
      console.error("Submission error:", error);

      const errorMessages =
        error.response?.data?.details &&
          Array.isArray(error.response.data.details)
          ? error.response.data.details
            .map(
              (detail: { field: string; message: string }) =>
                `<b>${detail.field}</b>: ${detail.message}`
            )
            .join("<br>")
          : error.response?.data?.errors &&
            Array.isArray(error.response.data.errors)
            ? error.response.data.errors
              .map((message: string) => `${message}`)
              .join("<br>")

            : error.response?.data?.message || "An unexpected error occurred";


      Swal.fire({
        title: "Error!",
        html: errorMessages,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async (values: PitchFormValues) => {
    setIsDrafting(true);

    const submissionPayload = {
      ...values,
      status: "draft",
      selectedPlatforms: Object.entries(selectedDSPs)
        .filter(([_, isSelected]) => isSelected)
        .map(([platform]) => platform),
    };

    try {
      const response = await pitchesApi.saveDraft(
        transformSubmissionData(submissionPayload)
      );

      if (!response?.data.success) {
        throw new Error(response.statusText || "Draft save failed");
      }

      Swal.fire({
        title: "Success!",
        text: "Draft saved successfully",
        icon: "success",
        confirmButtonText: "OK",
      });
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Draft save error:", error);

      Swal.fire({
        title: "Error!",
        text: error.response?.data?.message || "Failed to save draft",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsDrafting(false);
    }
  };

  // Function to scroll to the first error
  const scrollToFirstError = (errors: any) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;

    const firstErrorKey = errorKeys[0];
    const errorField = document.querySelector(`[name="${firstErrorKey}"]`);

    if (errorField) {
      errorField.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      errorField.classList.add('ring-2', 'ring-red-500', 'bg-red-50');
      setTimeout(() => {
        errorField.classList.remove('ring-2', 'ring-red-500', 'bg-red-50');
      }, 3000);
    }
  };

  // Function to show error toast
  const showErrorToast = (errors: any) => {
    const errorCount = Object.keys(errors).length;
    const errorFields = Object.keys(errors).slice(0, 3).join(', ');
    const remainingCount = errorCount > 3 ? errorCount - 3 : 0;

    let errorMessage = `Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''}.`;
    if (errorCount <= 3) {
      errorMessage += ` Fields: ${errorFields}`;
    } else {
      errorMessage += ` Fields: ${errorFields}${remainingCount > 0 ? ` and ${remainingCount} more` : ''}`;
    }

    Swal.fire({
      title: 'Validation Errors',
      text: errorMessage,
      icon: 'error',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
      }
    });
  };

  // Create dynamic validation schema based on selected DSPs
  const validationSchema = React.useMemo(() => {
    return createPitchValidationSchema(selectedDSPs);
  }, [selectedDSPs]);

  return (
    <div className="p-6 rounded-lg shadow-lg bg-gray-50">
      <button
        onClick={() => navigate("/dashboard")}
        className="flex items-center mb-4 text-gray-600 hover:text-gray-800"
      >
        <ArrowLeftIcon className="w-5 h-5 mr-2" /> Back
      </button>

      {/* Remove the Error Summary Banner section */}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors }) => (
          <Form>
            <div className="p-5 mb-8 rounded-lg bg-gray-50">
              <ReleaseInfo />
            </div>

            <div className="p-5 mb-8 rounded-lg bg-gray-50">
              <PlatformSelection
                selectedDSPs={selectedDSPs}
                setSelectedDSPs={setSelectedDSPs}
              />
            </div>

            <div className="p-5 mb-8 rounded-lg bg-gray-50">
              <GeneralReleaseInfo />
            </div>

            <div className="p-5 mb-8 rounded-lg bg-gray-50">
              <DspSpecificInfo selectedDSPs={selectedDSPs} />
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="px-6 py-2 text-white bg-gray-400 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isSubmitting || isDrafting}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => handleSaveDraft(values)}
                disabled={isSubmitting || isDrafting}
                className="px-6 py-2 text-white rounded-md bg-cyan-500 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDrafting ? (
                  <>
                    <span className="inline-block w-4 h-4 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></span>
                    Saving Draft...
                  </>
                ) : (
                  "Save Draft"
                )}
              </button>

              <button
                type="submit"
                onClick={() => {
                  if (Object.keys(errors).length > 0) {
                    showErrorToast(errors);
                    scrollToFirstError(errors);
                  }
                }}
                disabled={isSubmitting || isDrafting}
                className="px-6 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <span className="inline-block w-4 h-4 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></span>
                    Submitting...
                  </>
                ) : (
                  "Submit Pitch"
                )}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default FormPage;